/**
 * POM-6537 — "Bulk Lot Update" in Review Lot & NDC: which NDCs one lot can be given to, what the
 * lot is picked against, and which lots are on offer there.
 *
 * One choice per product and NDC with at least two rows a lot can be given to; a single row is
 * what its own pencil is for. A lot belongs to one NDC, so the NDC is chosen first and the rows
 * offered are only that NDC's. A facility with no lot to pick and no way to record one by hand is
 * not offered, and nor is a choice left with none: an option that leads nowhere only raises the
 * question of why. Choices are A–Z by drug, then NDC, and their facilities A–Z.
 *
 * Pure: no React, no network.
 */

import { canTakeBulkLot } from './bulkApplyPlan';
import { withBatchShare } from './batchLotUsage';
import { ALL, compareAlphabetically } from './bulkLotNdcReviewFiltering';
import { REVIEW_GROUP, reviewGroupFor } from './bulkLotNdcReviewGroups';
import { effectiveNdcFor, selectableLotsByNdc, toDateOnly } from './bulkLotNdcStatus';
import { isRecordableNdc } from './bulkManualLotEntry';
import { ndcDigits } from './bulkOverrideFilters';

const MIN_ROWS_FOR_BULK = 2;

/** `[{ fulfillingPharmacyId, facilityName, rowCount }]`, A–Z by name. */
const countRowsPerFacility = (items) => {
  const byId = new Map();
  items.forEach((item) => {
    const facility = byId.get(item.fulfillingPharmacyId) ?? {
      fulfillingPharmacyId: item.fulfillingPharmacyId,
      facilityName: item.fulfillingPharmacyName || 'Unknown facility',
      rowCount: 0,
    };
    facility.rowCount += 1;
    byId.set(item.fulfillingPharmacyId, facility);
  });

  return [...byId.values()].sort((left, right) =>
    compareAlphabetically(left.facilityName, right.facilityName)
  );
};

const isInNeedsAttention = (row) => reviewGroupFor(row.status) === REVIEW_GROUP.NEEDS_ATTENTION;

/** A choice's counts and facilities, from the rows it is left with. */
const summariseChoice = ({ rows, ...choice }) => {
  const items = rows.map((row) => row.item);

  return {
    ...choice,
    rows,
    items,
    rowCount: rows.length,
    needsAttentionCount: rows.filter(isInNeedsAttention).length,
    facilities: countRowsPerFacility(items),
  };
};

const choiceKeyOf = ({ row, changes }) => {
  const { item } = row;
  const ndc = effectiveNdcFor({ item, change: changes?.[item.requestLineItemId] });

  return ndc && item.productId ? { key: `${item.productId}::${ndcDigits(ndc)}`, ndc } : null;
};

/**
 * `rows` are the review's rows, listed and unlisted, as the grid shows them; `changes` the staged
 * edits. Each choice carries the rows a lot can be given to (`rows`), for the anchor and for
 * keepPickableBulkLotUpdateChoices, and every row of its NDC whatever its state (`ndcRows`), for
 * the preview of what a lot would do.
 */
export const buildBulkLotUpdateChoices = ({ rows, changes }) => {
  const ndcRowsByKey = new Map();
  (rows ?? []).forEach((row) => {
    const { key } = choiceKeyOf({ row, changes }) ?? {};
    if (key) ndcRowsByKey.set(key, [...(ndcRowsByKey.get(key) ?? []), row]);
  });

  const byKey = new Map();
  (rows ?? []).filter(canTakeBulkLot).forEach((row) => {
    const { item } = row;
    const { key, ndc } = choiceKeyOf({ row, changes }) ?? {};
    if (!key) return;

    const choice = byKey.get(key) ?? {
      key,
      ndc,
      productId: item.productId,
      drugName: [item.medicationName, item.strength].filter(Boolean).join(' '),
      rows: [],
      ndcRows: ndcRowsByKey.get(key),
    };
    choice.rows.push(row);
    byKey.set(key, choice);
  });

  return [...byKey.values()]
    .filter((choice) => choice.rows.length >= MIN_ROWS_FOR_BULK)
    .map(summariseChoice)
    .sort(
      (left, right) =>
        compareAlphabetically(left.drugName, right.drugName) ||
        compareAlphabetically(left.ndc, right.ndc)
    );
};

/** The choice to open on: the only one, else the one the grid's NDC filter is already on. */
export const findInitialBulkLotUpdateChoice = ({ choices, ndcFilter }) => {
  if (choices.length === 1) return choices[0];
  if (!ndcFilter || ndcFilter === ALL) return null;

  return choices.find((choice) => ndcDigits(choice.ndc) === ndcDigits(ndcFilter)) ?? null;
};

/** The facility with the most of the choice's rows; A–Z breaks a tie. */
export const findBusiestFacility = (choice) =>
  (choice?.facilities ?? []).reduce(
    (busiest, facility) => (!busiest || facility.rowCount > busiest.rowCount ? facility : busiest),
    null
  );

/**
 * What the lot is picked against: the choice's product and NDC at one facility, standing in for the
 * edited row of Change NDC & lot (bulkApplyPlan's `editedItem`). It edits no row, so it has no
 * requestLineItemId and records no lot. Its fill is the smallest there, so without a claim a lot
 * that covers no row is not offered. It may record by hand, and offer a short stock lot, when any
 * row there may record System Deficit, as any such row claims the rest of one (bulkApplyPlan).
 */
export const buildBulkLotUpdateAnchor = ({ choice, facility }) => {
  const rowsThere = choice.rows.filter(
    (row) => row.item.fulfillingPharmacyId === facility.fulfillingPharmacyId
  );
  const items = rowsThere.map((row) => row.item);

  return {
    requestLineItemId: null,
    productId: choice.productId,
    medicationName: choice.drugName,
    effectiveNdc: choice.ndc,
    fulfillingPharmacyId: facility.fulfillingPharmacyId,
    fulfillingPharmacyName: facility.facilityName,
    recordedLots: [],
    fillQuantity: String(Math.min(...items.map((item) => Number(item.fillQuantity || 0)))),
    canRecordUnverifiedLot: items.some((item) => item.canRecordUnverifiedLot === true),
  };
};

/** May a short stock lot be offered there: the switch, and a row there may claim the rest. */
export const isBulkShortfallAllowed = ({ anchor, isManualLotEnabled }) =>
  isManualLotEnabled === true && anchor?.canRecordUnverifiedLot === true;

/** May a lot be recorded by hand there: the same conditions Change NDC & lot asks. */
export const canRecordBulkLotByHand = ({ anchor, recordable, isManualLotEnabled }) =>
  isManualLotEnabled === true &&
  anchor?.canRecordUnverifiedLot === true &&
  !!recordable?.hasInventoryMaster &&
  isRecordableNdc({ ndc: anchor?.effectiveNdc, ndcOptions: recordable?.ndcOptions });

/**
 * The anchor NDC's lots at its facility with the batch taken off, earliest expiry first — the
 * picker's own rules (selectableLotsByNdc, withBatchShare) for the anchor's smallest fill.
 */
const findBulkLotUpdateLots = ({ anchor, groups, batchLotShare, todayStr, isManualLotEnabled }) => {
  if (!anchor) return [];

  const shortfallAllowed = isBulkShortfallAllowed({ anchor, isManualLotEnabled });
  const usableByNdc = withBatchShare({
    lotsByNdc: selectableLotsByNdc({
      groups,
      fillQuantity: anchor.fillQuantity,
      todayStr,
      allowShortfall: shortfallAllowed,
    }),
    shareByLotId: batchLotShare,
    fillQuantity: anchor.fillQuantity,
    shortfallAllowed,
  });
  const [, lots = []] =
    [...usableByNdc].find(([ndc]) => ndcDigits(ndc) === ndcDigits(anchor.effectiveNdc)) ?? [];

  return [...lots].sort((a, b) => new Date(a.expirationDate) - new Date(b.expirationDate));
};

/**
 * The choices with only the facilities where something can be picked: a lot in stock, or one
 * recorded by hand. A row at any other facility can take no lot (each takes its own facility's
 * record of it), so it is not counted, and a choice left with fewer than two rows is dropped.
 * `availabilityFor`, `recordableFor` and `batchLotShareFor` take an item, as the review dialog's do.
 */
export const keepPickableBulkLotUpdateChoices = ({
  choices,
  availabilityFor,
  recordableFor,
  batchLotShareFor,
  todayStr,
  isManualLotEnabled,
}) =>
  choices
    .map((choice) => {
      const pickableFacilityIds = new Set(
        choice.facilities
          .filter((facility) => {
            const anchor = buildBulkLotUpdateAnchor({ choice, facility });
            const lots = findBulkLotUpdateLots({
              anchor,
              groups: availabilityFor(anchor)?.groups ?? [],
              batchLotShare: batchLotShareFor(anchor),
              todayStr,
              isManualLotEnabled,
            });

            return (
              lots.length > 0 ||
              canRecordBulkLotByHand({
                anchor,
                recordable: recordableFor(anchor),
                isManualLotEnabled,
              })
            );
          })
          .map((facility) => facility.fulfillingPharmacyId)
      );

      return summariseChoice({
        ...choice,
        rows: choice.rows.filter((row) => pickableFacilityIds.has(row.item.fulfillingPharmacyId)),
      });
    })
    .filter((choice) => choice.rowCount >= MIN_ROWS_FOR_BULK);

/** Lot number and BUD: what one lot is at every facility (POM-6506 AC 4). */
const lotIdentityOf = (lot) =>
  `${String(lot.lotNumber ?? '')
    .trim()
    .toLowerCase()}::${toDateOnly(lot.expirationDate) ?? ''}`;

/** Ids of the facilities holding any record of the lot for the NDC, usable or not. */
const findFacilitiesHoldingLot = ({ items, availabilityFor, ndc, lotKey }) =>
  new Set(
    items
      .filter((item) =>
        (availabilityFor(item)?.groups ?? [])
          .filter((group) => ndcDigits(group.ndc) === ndcDigits(ndc))
          .flatMap((group) => group.lots ?? [])
          .some((lot) => lotIdentityOf(lot) === lotKey)
      )
      .map((item) => item.fulfillingPharmacyId)
  );

/**
 * The NDC's lots across the choice's facilities, one entry per lot number and BUD, earliest
 * expiry first: where each is on offer, with what the batch leaves of it there (`atFacilities`),
 * and which facilities hold a record of it at all (`stockedFacilityIds`, over every row of the
 * NDC), so a facility with too little of it is not said not to stock it. Each row then takes its
 * own facility's record of the lot, so no facility has to be picked first.
 *
 * A lot is picked at the facility offering it with the most of the choice's rows, A–Z breaking a
 * tie (`pickedAt`): the plan takes that facility's very record, and the record with the same lot
 * number and BUD at every other. The lot is shown as that record spells it.
 */
export const findMergedBulkLots = ({
  choice,
  availabilityFor,
  batchLotShareFor,
  todayStr,
  isManualLotEnabled,
}) => {
  if (!choice) return [];

  const atFacilitiesByIdentity = new Map();
  choice.facilities.forEach((facility) => {
    const anchor = buildBulkLotUpdateAnchor({ choice, facility });
    const groups = availabilityFor(anchor)?.groups ?? [];
    // One facility can hold two records under one lot number and BUD. Only the first on file is
    // offered, usable or not, as the plan takes the first at any facility but the anchor's.
    const firstRecordIdByIdentity = new Map();
    groups
      .filter((group) => ndcDigits(group.ndc) === ndcDigits(choice.ndc))
      .flatMap((group) => group.lots ?? [])
      .forEach((lot) => {
        const key = lotIdentityOf(lot);
        if (!firstRecordIdByIdentity.has(key)) firstRecordIdByIdentity.set(key, lot.inventoryLotId);
      });

    findBulkLotUpdateLots({
      anchor,
      groups,
      batchLotShare: batchLotShareFor(anchor),
      todayStr,
      isManualLotEnabled,
    })
      .filter((lot) => firstRecordIdByIdentity.get(lotIdentityOf(lot)) === lot.inventoryLotId)
      .forEach((lot) => {
        const key = lotIdentityOf(lot);
        atFacilitiesByIdentity.set(key, [
          ...(atFacilitiesByIdentity.get(key) ?? []),
          { facility, lot },
        ]);
      });
  });

  // One item per facility the NDC's rows fill at, to ask its availability.
  const itemPerFacility = [
    ...new Map(
      (choice.ndcRows ?? choice.rows).map(({ item }) => [item.fulfillingPharmacyId, item])
    ).values(),
  ];

  return [...atFacilitiesByIdentity]
    .map(([key, atFacilities]) => {
      const busiest = findBusiestFacility({ facilities: atFacilities.map((at) => at.facility) });
      const pickedAt = atFacilities.find(
        (at) => at.facility.fulfillingPharmacyId === busiest.fulfillingPharmacyId
      );

      return {
        key,
        lotNumber: pickedAt.lot.lotNumber,
        expirationDate: pickedAt.lot.expirationDate,
        daysUntilExpiry: pickedAt.lot.daysUntilExpiry,
        atFacilities,
        pickedAt,
        stockedFacilityIds: findFacilitiesHoldingLot({
          items: itemPerFacility,
          availabilityFor,
          ndc: choice.ndc,
          lotKey: key,
        }),
      };
    })
    .sort((a, b) => new Date(a.expirationDate) - new Date(b.expirationDate));
};
